import React, { useState } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    Chip,
    IconButton,
    Tooltip
} from '@mui/material';
import { Edit, Trash2 } from 'react-feather';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';

const CategoryList = ({ categories, problemTypes, onEdit, onDelete }) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);

    // Get problem type name
    const getProblemTypeName = (typeId) => {
        const problemType = problemTypes.find(pt => pt.id === typeId);
        return problemType ? problemType.problem_type : 'Unknown';
    };

    // Handle page change
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Open delete confirmation dialog
    const openDeleteConfirm = (category) => {
        setCategoryToDelete(category);
        setDeleteConfirmOpen(true);
    };

    // Close delete confirmation dialog
    const closeDeleteConfirm = () => {
        setDeleteConfirmOpen(false);
        setCategoryToDelete(null);
    };

    // Confirm delete
    const confirmDelete = () => {
        if (categoryToDelete) {
            onDelete(categoryToDelete.id);
            closeDeleteConfirm();
        }
    };

    // Paginated categories
    const paginatedCategories = categories.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <>
            <Paper
                elevation={0}
                sx={{
                    borderRadius: '10px',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    overflow: 'hidden',
                    mb: 3
                }}
            >
                <TableContainer>
                    <Table>
                        <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Problem Type</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Date Created</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedCategories.map((category) => (
                                <TableRow key={category.id} hover>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                            {category.problem}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={getProblemTypeName(category.problem_type)}
                                            size="small"
                                            sx={{
                                                backgroundColor: '#E0E7FF',
                                                color: '#4F46E5',
                                                borderRadius: '4px',
                                                fontSize: '0.75rem',
                                                fontWeight: 500
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {new Date(category.date_created).toLocaleString()}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <Tooltip title="Edit">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => onEdit(category)}
                                                    sx={{ mr: 1 }}
                                                >
                                                    <Edit size={16} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => openDeleteConfirm(category)}
                                                    color="error"
                                                >
                                                    <Trash2 size={16} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {categories.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                        <Typography variant="body2" color="textSecondary">
                                            No categories found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={categories.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* Delete Confirmation Dialog */}
            <ConfirmDeleteDialog
                open={deleteConfirmOpen}
                onClose={closeDeleteConfirm}
                onConfirm={confirmDelete}
                title="Delete Category"
                message="Are you sure you want to delete this category?"
                warningMessage="This action cannot be undone. Any tickets using this category will need to be reassigned."
                itemContent={
                    categoryToDelete && (
                        <>
                            <Typography variant="subtitle2">
                                {categoryToDelete.problem}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                {getProblemTypeName(categoryToDelete.problem_type)}
                            </Typography>
                        </>
                    )
                }
            />
        </>
    );
};

export default CategoryList;